import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Zap, Mail,
  Pickaxe, Building2, HeartPulse, Landmark, Factory, Fuel, PawPrint,
} from 'lucide-react';
import { INDUSTRIES_PAGE, CONTACT } from '../mock';
import Navbar from './Navbar';
import SiteFooter from './SiteFooter';

const ICONS = { Pickaxe, Building2, HeartPulse, Landmark, Factory, Fuel, PawPrint };
const slugify = (s) => s.toLowerCase().replace(/\s+/g, '-');

export default function IndustriesPage() {
  const { hero, items, outro } = INDUSTRIES_PAGE;

  useEffect(() => {
    document.title = 'Industries — Electronico Holdings';
    const hash = window.location.hash;
    if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        setTimeout(() => {
          window.scrollTo({ top: el.getBoundingClientRect().top + window.pageYOffset - 100, behavior: 'smooth' });
          el.classList.add('ring-2', 'ring-[#0ef3fe]');
          setTimeout(() => el.classList.remove('ring-2', 'ring-[#0ef3fe]'), 2000);
        }, 300);
        return;
      }
    }
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="relative min-h-screen bg-[#0a1526] text-white">
      <Navbar />

      {/* HERO */}
      <section className="relative pt-36 pb-20 px-5 overflow-hidden grid-bg">
        <div className="blob bg-[#0a6bd6] w-[460px] h-[460px] -top-24 -left-24 animate-pulse-glow" />
        <div className="blob bg-[#0ef3fe] w-[360px] h-[360px] top-20 right-0 opacity-40" />
        <div className="relative z-10 max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full neon-border px-4 py-1.5 mb-6">
              <Factory className="h-4 w-4 text-[#0a6bd6]" />
              <span className="font-mono-accent text-xs text-white/80">{hero.kicker}</span>
            </div>
            <h1 className="font-display text-4xl md:text-6xl font-bold leading-[1.08] mb-6">
              Built for <span className="gradient-text animate-gradient">Every Environment</span>
            </h1>
            <p className="text-lg text-white/65 max-w-xl mb-8 leading-relaxed">{hero.subtitle}</p>
            <div className="flex flex-wrap gap-4">
              <a href="#sectors" className="group inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-[#0a6bd6] to-[#0ef3fe] text-black px-7 py-3.5 font-semibold hover:shadow-[0_0_30px_rgba(10,107,214,0.5)] transition-shadow">
                Explore Sectors <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </a>
              <Link to="/" className="inline-flex items-center gap-2 rounded-xl neon-border px-7 py-3.5 font-semibold text-white hover:bg-white/5 transition-colors">
                <ArrowLeft className="h-4 w-4" /> Home
              </Link>
            </div>
            <div className="grid grid-cols-4 gap-4 mt-12 max-w-lg">
              {hero.stats.map((s) => (
                <div key={s.label}>
                  <div className="font-display text-2xl md:text-3xl font-bold text-glow-cyan">{s.value}</div>
                  <div className="text-xs text-white/50 mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="relative animate-floaty">
            <div className="absolute -inset-4 bg-gradient-to-tr from-[#0a6bd6]/30 to-[#0ef3fe]/30 blur-2xl rounded-3xl" />
            <div className="relative rounded-3xl overflow-hidden neon-border">
              <img src={hero.image} alt="Industries" className="w-full h-[440px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a1526] via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between">
                <span className="font-mono-accent text-xs text-white">// SECTOR READY</span>
                <span className="flex items-center gap-2 text-xs text-white/70">
                  <span className="h-2 w-2 rounded-full bg-[#5bd0ff] animate-pulse-glow" /> Deployed
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTORS GRID */}
      <section id="sectors" className="relative py-20 px-5">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="font-mono-accent text-xs text-[#0ef3fe]">// SECTORS</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold mt-3">Industries We Serve</h2>
            <p className="text-white/55 mt-4 max-w-xl mx-auto">Tailored solutions engineered for the demands of each sector.</p>
          </div>
          <div className="flex flex-wrap justify-center gap-6">
            {items.map((ind) => {
              const Icon = ICONS[ind.icon];
              return (
                <div
                  key={ind.name}
                  id={`sector-${slugify(ind.name)}`}
                  className="group relative overflow-hidden neon-border rounded-3xl h-64 w-full sm:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)] hover:-translate-y-2 transition-all duration-300 scroll-mt-28"
                >
                  <img src={ind.image} alt={ind.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a1526] via-[#0a1526]/40 to-transparent" />
                  <span className="absolute top-5 left-5 flex h-11 w-11 items-center justify-center rounded-xl backdrop-blur-md" style={{ backgroundColor: `${ind.color}26` }}>
                    <Icon className="h-6 w-6" style={{ color: ind.color }} />
                  </span>
                  <h3 className="absolute bottom-6 left-6 right-6 font-display text-2xl font-bold text-white">{ind.name}</h3>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20 px-5 grid-bg">
        <div className="max-w-5xl mx-auto">
          <div className="relative neon-border rounded-3xl p-10 md:p-16 overflow-hidden text-center">
            <div className="blob bg-[#0a6bd6] w-80 h-80 -top-20 -left-10 opacity-30" />
            <div className="blob bg-[#0ef3fe] w-80 h-80 -bottom-20 -right-10 opacity-30" />
            <div className="relative z-10">
              <h2 className="font-display text-3xl md:text-5xl font-bold mb-4">A Solution for Your Sector</h2>
              <p className="text-white/60 max-w-2xl mx-auto mb-8">{outro}</p>
              <div className="flex flex-wrap justify-center gap-4">
                <a href={`mailto:${CONTACT.email}`} className="group inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-[#0a6bd6] to-[#0ef3fe] text-black px-7 py-3.5 font-semibold hover:shadow-[0_0_30px_rgba(10,107,214,0.5)] transition-shadow">
                  <Mail className="h-5 w-5" /> Talk to Us
                </a>
                <Link to="/" className="inline-flex items-center gap-2 rounded-xl neon-border px-7 py-3.5 font-semibold text-white hover:bg-white/5 transition-colors">
                  <ArrowLeft className="h-4 w-4" /> Back Home
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <SiteFooter />
    </div>
  );
}
